import { 
  Box, 
  Paper, 
  Typography,
  Stack,
  Avatar,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

interface Performer {
  member_name: string;
  total_xp: number;
  avg_score: string | number;
  quizzes_completed: string | number;
}

interface QuizLeaderboardProps {
  performers: Performer[];
  title?: string;
}

const getMedalColor = (index: number) => {
  switch (index) {
    case 0: return '#FFD700'; // Gold
    case 1: return '#C0C0C0'; // Silver
    case 2: return '#CD7F32'; // Bronze
    default: return '#9CA3AF';
  }
};

const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const getRankDisplay = (index: number) => {
  if (index < 3) {
    return (
      <EmojiEventsIcon 
        sx={{ 
          fontSize: 28, 
          color: getMedalColor(index),
          filter: index === 0 ? 'drop-shadow(0 2px 4px rgba(255, 215, 0, 0.4))' : 'none',
        }} 
      />
    );
  }
  return (
    <Typography 
      sx={{ 
        fontWeight: 700, 
        color: '#9CA3AF',
        fontSize: '1.25rem',
        width: 28,
        textAlign: 'center',
      }}
    >
      {index + 1}
    </Typography>
  );
};

export const QuizLeaderboard = ({ performers, title = 'Top Performers' }: QuizLeaderboardProps) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #E5E7EB',
        overflow: 'hidden',
        height: '100%',
      }}
    >
      <Box sx={{ p: 3, borderBottom: '1px solid #E5E7EB' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <EmojiEventsIcon sx={{ color: '#F59E0B' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#2C3E50' }}>
            {title}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5 }}>
          Ranked by total XP earned
        </Typography>
      </Box>

      <Stack spacing={0} sx={{ p: 2 }}>
        {performers.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#9CA3AF' }}>
              No quiz data yet
            </Typography>
          </Box>
        ) : (
          performers.map((performer, index) => (
            <Stack
              key={index}
              direction="row"
              alignItems="center"
              spacing={2}
              sx={{
                py: 2,
                px: 1,
                borderRadius: '12px',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: '#F9FAFB',
                },
                ...(index === 0 && {
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FEF3C7',
                }),
              }}
            >
              <Box sx={{ width: 32, display: 'flex', justifyContent: 'center' }}>
                {getRankDisplay(index)}
              </Box>

              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  backgroundColor: index === 0 ? '#F59E0B' : 
                                   index === 1 ? '#6B7280' : 
                                   index === 2 ? '#D97706' : '#0099CC',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                {getInitials(performer.member_name)}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600, 
                    color: '#2C3E50',
                  }}
                >
                  {performer.member_name}
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    {performer.quizzes_completed} quizzes
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                    {performer.avg_score}% avg
                  </Typography>
                </Stack>
              </Box>

              <Box sx={{ textAlign: 'right' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700, 
                    color: index === 0 ? '#F59E0B' : '#2C3E50',
                  }}
                >
                  {performer.total_xp}
                </Typography>
                <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
                  XP
                </Typography>
              </Box>
            </Stack>
          ))
        )}
      </Stack>
    </Paper>
  );
};
